import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelLocation } from "@/lib/hotelLocations";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  open: { en: "Open route in Google Maps", fr: "Itinéraire sur Google Maps", es: "Abrir ruta en Google Maps" },
  location: { en: "Property location", fr: "Emplacement du site", es: "Ubicación de la propiedad" },
  coming: { en: "Location coming soon", fr: "Localisation à venir", es: "Ubicación próximamente" },
};

// Morocco centroid → used to detect "no per-slug coords yet".
const MOROCCO_CENTROID = [31.7917, -7.0926];

// Branded pin matching PropertyCardMap so the two visuals feel one system.
const PIN_ICON = L.divIcon({
  className: "xaluca-about-pin",
  html: `
    <div style="position:relative;width:28px;height:28px;">
      <span style="
        position:absolute;inset:0;border-radius:50%;
        background:#C16542;
        box-shadow:0 0 0 4px #FDFBF7, 0 6px 14px rgba(0,0,0,0.5);
      "></span>
      <span style="
        position:absolute;left:50%;top:50%;width:8px;height:8px;border-radius:50%;
        background:#FDFBF7;transform:translate(-50%,-50%);
      "></span>
    </div>
  `,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Interactive mini-map sized to sit inside the About column below the
// image grid. Drag + tap enabled so guests can peek at nearby villages;
// scroll-wheel zoom stays OFF so the page keeps scrolling as expected.
const HotelAboutMap = ({ hotelSlug, hotelName, hotelLocation }) => {
  const { lang } = useLanguage();
  const wrapRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "160px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const { coords, hasRealCoords, gmapsUrl, address } = useMemo(() => {
    const loc = getHotelLocation(hotelSlug, { location: hotelLocation });
    const c = loc.coords;
    const isFallback =
      Array.isArray(c) &&
      c[0] === MOROCCO_CENTROID[0] &&
      c[1] === MOROCCO_CENTROID[1];
    const dest = encodeURIComponent(`${hotelName} — ${pick(loc.address, lang)}`);
    return {
      coords: c,
      hasRealCoords: !!c && !isFallback,
      address: pick(loc.address, lang),
      gmapsUrl: c
        ? `https://www.google.com/maps/dir/?api=1&destination=${c[0]},${c[1]}&destination_place_id=${dest}`
        : null,
    };
  }, [hotelSlug, hotelName, hotelLocation, lang]);

  if (!hasRealCoords) {
    return (
      <div
        ref={wrapRef}
        className="mt-3 relative w-full max-w-full overflow-hidden border border-[#E8DFCF] bg-[#F2EBE1]/60 aspect-[4/3] flex flex-col items-center justify-center px-6 text-center"
        data-testid={`about-map-${hotelSlug}-placeholder`}
      >
        <MapPin className="w-5 h-5 text-[#5C5248]/60 mb-2" strokeWidth={1.5} />
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/75">
          {pick(COPY.coming, lang)}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="mt-3 relative w-full max-w-full overflow-hidden border border-[#E8DFCF] bg-[#E8DFCF] aspect-[4/3]"
      data-testid={`about-map-${hotelSlug}`}
      data-cms-scope={`hotel.${hotelSlug}.about-map`}
    >
      {inView ? (
        <>
          <MapContainer
            center={coords}
            zoom={14}
            minZoom={5}
            maxZoom={18}
            zoomControl={false}
            attributionControl={false}
            scrollWheelZoom={false}
            dragging
            doubleClickZoom
            touchZoom
            className="absolute inset-0 w-full h-full"
            data-testid={`about-map-${hotelSlug}-leaflet`}
          >
            <ZoomControl position="bottomleft" />
            <TileLayer
              attribution=""
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={18}
              detectRetina
            />
            <Marker position={coords} icon={PIN_ICON} interactive={false} />
          </MapContainer>

          {/* Top overlay — property name label */}
          <div
            className="pointer-events-none absolute top-3 left-3 z-[400] max-w-[calc(100%-6rem)] px-3 py-1.5 bg-[#FDFBF7]/95 backdrop-blur-sm"
            data-testid={`about-map-${hotelSlug}-label`}
          >
            <p className="text-[9px] tracking-[0.24em] uppercase text-[#C16542]">
              {pick(COPY.location, lang)}
            </p>
            <p className="font-serif-x text-sm md:text-base text-[#1A1513] leading-tight mt-0.5 truncate">
              {hotelName}
            </p>
          </div>

          {/* Subtle vignette so the CTA pill always reads over any tile */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#1A1513]/25 via-transparent to-transparent" />

          {gmapsUrl ? (
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={address}
              className="absolute bottom-3 right-3 z-[400] inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[9px] md:text-[10px] tracking-[0.22em] uppercase shadow-md transition-colors"
              data-testid={`about-map-${hotelSlug}-open`}
            >
              <ExternalLink className="w-3 h-3" strokeWidth={2} />
              {pick(COPY.open, lang)}
            </a>
          ) : null}
        </>
      ) : (
        <div className="absolute inset-0 bg-[#E8DFCF]" aria-hidden="true" />
      )}
    </div>
  );
};

export default HotelAboutMap;
